import React from 'react';
import { useArgoPrefs } from '@amzn/awsscm-argo-core/argo/utils/ArgoPreference';
import { NavigableApplicationsObject } from '../components/RecentApps/types';
import { PREFERENCE_KEYS } from '../resources/common_constants';

const EMPTY_ARRAY: NavigableApplicationsObject[] = [];
const MAX_RECENT_APPS = 8;

export function useRecentApps() {
  const [recentlyUsedLinks, setRecentlyUsedLinks, loading, error] = useArgoPrefs<NavigableApplicationsObject[]>(PREFERENCE_KEYS.recentAppsPreferenceKey, EMPTY_ARRAY);

  const updateRecentlyUsedLinks = React.useCallback(async (clickedApp: NavigableApplicationsObject) => {
    try {
      const currentLinks = recentlyUsedLinks || EMPTY_ARRAY;
      
      const existingAppIndex = currentLinks.findIndex(app => app.navTitle === clickedApp.navTitle);
      if (existingAppIndex !== -1) {
        clickedApp.isPinned = currentLinks[existingAppIndex].isPinned;
      }
      
      const filteredLinks = currentLinks.filter(app => app.navTitle !== clickedApp.navTitle);
      
      const updatedLinks = [clickedApp, ...filteredLinks];
      
      const pinnedApps = updatedLinks.filter(app => app.isPinned);
      const nonPinnedApps = updatedLinks.filter(app => !app.isPinned);
      
      const recentNonPinnedApps = nonPinnedApps.slice(0, MAX_RECENT_APPS - pinnedApps.length);
      
      const recentLinks = [...pinnedApps, ...recentNonPinnedApps];
      
      await setRecentlyUsedLinks(recentLinks);
    } catch (err) {
      console.error('Error updating recent apps:', err);
    }
  }, [recentlyUsedLinks, setRecentlyUsedLinks]);

  const togglePinApp = React.useCallback(async (appToToggle: NavigableApplicationsObject) => {
    try {
      const currentLinks = recentlyUsedLinks || EMPTY_ARRAY;
      
      const updatedLinks = currentLinks.map(app => {
        if (app.navTitle === appToToggle.navTitle) {
          return { ...app, isPinned: !app.isPinned };
        }
        return app;
      });
      
      await setRecentlyUsedLinks(updatedLinks);
    } catch (err) {
      console.error('Error toggling pin status:', err);
    }
  }, [recentlyUsedLinks, setRecentlyUsedLinks]);

  return React.useMemo(() => ({
    recentlyUsedLinks: recentlyUsedLinks || EMPTY_ARRAY,
    updateRecentlyUsedLinks,
    togglePinApp,
    loading,
    error
  }), [recentlyUsedLinks, updateRecentlyUsedLinks, togglePinApp, loading, error]);
}
